import React, { ReactElement, useEffect, useMemo, useRef } from 'react';
import { Progress } from 'antd';
import { isEqual } from 'lodash';
import '../ReportingAndAnalytics.less';
import { MetricCardGroupProps } from '../../../shared/interfaces/reportingAndAnalyticsInterface';
import { REPORTING_CONSTANTS } from './reportingAndAnalyticsConstants';

export const MetricCardGroup: React.FC<MetricCardGroupProps> = ({
  data,
  config,
  getSections,
  setActiveSection,
  availableStatus = [],
}: any): ReactElement => {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevSectionsRef = useRef<{ key: string; label: string }[]>([]);
  const statusLabelMap = useMemo(
    (): Record<string, string> =>
      Object?.fromEntries(
        (availableStatus ?? [])
          ?.filter((status: any): boolean => status?.value != null)
          ?.map((status: any): any[] => [
            String(status?.value),
            status?.label ?? '',
          ]),
      ),
    [availableStatus],
  );

  const navSections = useMemo(
    (): { key: string; label: string }[] =>
      Object?.keys(data || {})?.map(
        (sectionKey: any): { key: string; label: string } => ({
          key: sectionKey,
          label:
            statusLabelMap[sectionKey] ||
            config?.[sectionKey]?.Total?.title ||
            sectionKey,
        }),
      ),
    [data, config, statusLabelMap],
  );

  useEffect((): void => {
    if (!isEqual(prevSectionsRef?.current, navSections)) {
      getSections(navSections);
      prevSectionsRef.current = navSections;
    }
  }, [navSections, getSections]);

  const scrollIntoView = (index: number): void => {
    const ref = sectionRefs?.current?.[index];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect((): any => {
    // If setActiveSection is not provided, no need to proceed
    if (!setActiveSection) {
      return;
    }

    // Create an IntersectionObserver to watch which section is visible in viewport
    const viewer = new IntersectionObserver(
      (entries: any): void => {
        const visibleEntry = entries
          ?.filter((entry: any): boolean => entry?.isIntersecting)
          ?.sort(
            (firstElement: any, secondElement: any): number =>
              firstElement.boundingClientRect.top - secondElement.boundingClientRect.top,
          )[0];

        if (visibleEntry?.target) {
          // Get the id of the visible section element
          const visibleId = visibleEntry?.target?.getAttribute('id');

          // Update active section state if an id exists
          if (visibleId) {
            setActiveSection(visibleId);
          }
        }
      },
      {
        threshold: REPORTING_CONSTANTS.NAVIGATION_FIRING_THRESHOLD, // fire multiple times

        // Adjust root margin to consider section "visible"
        // earlier/later based on vertical space
        rootMargin: REPORTING_CONSTANTS.NAVIGATION_ROOT_MARGIN, // triggers when section is near top
      },
    );

    // Start observing each section reference
    sectionRefs?.current?.forEach(
      (element: any): boolean => element && viewer?.observe(element),
    );

    // Cleanup: stop observing when component unmounts or dependencies change
    return (): void => {
      sectionRefs?.current?.forEach(
        (element: any): boolean => element && viewer?.unobserve(element),
      );
    };
  }, [navSections]);

  return (
    <div className="metric-group-wrapper">
      {Object?.entries(data || {})?.map(
        ([sectionKey, sectionMetrics]: any, index: number): ReactElement => {
        const sectionConfig = config?.[sectionKey]?.transactionFlows || {};

          return (
            <div
              key={sectionKey}
              className="section-wrapper"
              id={sectionKey}
              ref={(element: any): void => {
                sectionRefs.current[index] = element;
              }}
              onMouseEnter={(): void => scrollIntoView(index)}
            >
              <div className="section-card">
                <h3 className="section-title">
                  {statusLabelMap?.[sectionKey] ??
                    sectionConfig?.Total?.title ??
                    sectionKey}
                </h3>
                <div className="metrics-grid">
                  {Object?.entries(sectionMetrics || {})?.map(
                    ([metricKey, metricValue]: any): ReactElement => {
                      const metricConfigGroup = sectionConfig?.[metricKey] || {};
                      const displayData: Record<string, any> = Object?.values(metricConfigGroup)?.[0] || {};
                      return (
                        <div className="metric-card" key={metricKey}>
                          <div className="Transaction-card-title">
                            {displayData?.title || metricKey}
                          </div>

                          {metricValue?.showProgressBar &&
                          typeof metricValue?.percentage === 'number' ? (
                            <div
                              className="progress-wrapper custom-progress"
                              style={
                                {
                                  '--count-color': displayData?.color,
                                } as React.CSSProperties
                              }
                            >
                              <div className="progress-left">
                                <Progress
                                  type="circle"
                                  percent={Number(metricValue.percentage)}
                                  strokeWidth={11}
                                  size="small"
                                  strokeColor={displayData?.color || '#1890ff'}
                                  format={(percent?: number): ReactElement => (
                                    <span className="custom-progress-count">
                                      {percent}
                                    </span>
                                  )}
                                />
                              </div>
                              <div className="separator"/>
                              <div className="progress-right">
                                {metricValue?.count ?? 0}
                              </div>
                            </div>
                          ) : (
                            <div className="count-text">
                              {metricValue?.count ?? 0}
                            </div>
                          )}
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            </div>
          );
        },
      )}
      <div className="h-800" />
    </div>
  );
};
